import { clampDigits, digitFormat, digitsIn } from '@agavi/blocks';
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Check } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  STATUS_CLASS, useAnswerRefFormats, useAnswerRefInfo, useAnswerRefScope,
  useAnswerRefDigitRows,
} from './answer-ref-context';

import type { AnswerFormat } from '@agavi/blocks';
import type { NodeViewProps } from '@tiptap/core';

import { CommitField } from '@/components/ui/commit-field';
import { NumberField } from '@/components/ui/number-field';
import { POPUP_LEVEL } from '@/components/ui/popup-level';
import SelectMenu from '@/components/ui/select-menu';

/**
 * A REFERENCE TO WHAT THIS STUDENT ANSWERED EARLIER, inside authored prose.
 *
 * Built as a near-twin of `VariableChip`, deliberately — the teacher's mental model is that this
 * behaves almost exactly like a variable, and every contract that chip had to settle applies
 * here for the same reasons. The differences are only these:
 *
 *   DELIMITERS, as a variable chip wears `{name}`. This read as `[name]` only outside rich
 *   text for a while, on the reasoning that a link mark inside a tinted pill already said
 *   "reference" and brackets were a third way of saying it. That held while there were two
 *   concepts to tell apart; a third arrived — binding to an answer's possibility space — and
 *   the MARK turned out to be the redundant one. Brackets say the same thing and cost no
 *   glyph, which frees the chain to mean one thing again, on the scenario link.
 *
 *   It also makes the pill agree with everything else: `renderText` emits `[name]`, a formula
 *   field stores `[ara_…]`, the buttons are `[]`, and the clipboard's plain flavour is
 *   `[Kayak Velocity]`. The pill was the only surface not saying brackets.
 *
 *   A STATUS, which a variable has no need of. A variable cannot fail to exist; a reference can
 *   stop being reachable because someone moved a task, with nothing having been edited.
 *
 * The status is link-shaped rather than verdict-shaped on purpose. A green tick inside authored
 * question text, in a product whose whole visual language is correct/incorrect, reads as "this
 * answer is right" — which is not what it means.
 */

/**
 * How the chip asks to be renamed.
 *
 * The same shape `MathFormula` and `ReactionFormula` use: the node view does not own a dialog,
 * it hands the editor a current value and a commit callback. A text input living inside
 * contenteditable is a focus fight nobody needs to have twice.
 */
export interface AnswerRefChipOptions {
  /**
   * NONE. The chip edits the one thing it owns — how this occurrence reads — through its own
   * attribute, so nothing has to be handed in. It used to carry a rename callback, and
   * renaming is the one edit a chip is the wrong place for: a name belongs to the ALIAS and
   * is shared by every chip quoting it, so editing it from one occurrence edits something
   * the occurrence does not own. The Answer Recall panel lists aliases and renames them.
   */
  _?: never;
}

/**
 * Exported for its test, which drives the view directly rather than standing up an editor to
 * reach it — the questions here are all about what the chip offers and what it writes back,
 * and none of them is about Tiptap.
 */
export const AnswerRefChipNodeViewForTest = AnswerRefChipNodeView;

/** Movement past this is a drag; anything under it is a click. The figure `formula-field` uses. */
const DRAG_THRESHOLD_PX = 5;

function AnswerRefChipNodeView({ node, updateAttributes, editor, getPos }: NodeViewProps) {
  const { t } = useTranslation();
  const { aliasId, fallback, format, uncertaintyFormat } = node.attrs as {
    aliasId: string; fallback: string | null;
    format: AnswerFormat | null; uncertaintyFormat: AnswerFormat | null;
  };
  const info = useAnswerRefInfo(aliasId || null);
  const formats = useAnswerRefFormats(aliasId);
  const digitRowsFor = useAnswerRefDigitRows(aliasId);
  const takesDigits = digitRowsFor.value;
  const updateAlias = useAnswerRefScope()?.updateAlias;

  // No provider means this is not an authoring surface, so there is no status to show and none
  // should be shown — the student's render must never carry the teacher's iconography.
  const status = info?.status;
  // `fallback` is the last resort: a chip whose alias did not travel still says something.
  const name = info?.name ?? fallback ?? t('blocks.answerRef.missingSource');
  /*
   * CLICKING A CHIP CHOOSES HOW IT READS.
   *
   * It used to open the rename dialog, and renaming is the one thing a chip is the wrong
   * place for: a name belongs to the ALIAS and is shared by every chip quoting it, so
   * editing it from one occurrence is editing something the occurrence does not own. The
   * panel lists aliases and renames them there, which is where that belongs. Format is the
   * opposite — it is exactly this occurrence's business — so the gesture is now about the
   * thing the chip actually has.
   *
   * Gated on `info`, not on the options: `info` is absent outside authoring, since it comes
   * from the provider the task editor supplies and Preview does not. A student meeting a
   * formatting menu would be the worse half of the same mistake.
   */
  /*
   * EVERY CHIP ON AN AUTHORING SURFACE OPENS, including one with no formats to choose.
   *
   * It was gated on having formats, which read as a rule about formats and was really a rule
   * about who can edit: a reference to a plain number offers no second way to be read, so a
   * Numerical chip got a plain span and with it no way to name the thing — the one edit every
   * reference needs, and the only one some of them have.
   *
   * `info` is the gate instead, because it comes from the provider the task editor supplies
   * and Preview does not. A student meeting a rename field would be the worse half of the
   * same mistake.
   *
   * It also settles the alignment. Two branches meant two elements, a button and a span, with
   * the browser's own button defaults on one of them, so chips in one line of prose sat
   * differently depending on what their source happened to offer.
   */
  /* HOOKS, so they sit above the `canFormat` early return rather than beside the drag they
     belong to: that branch is Preview, and a hook skipped there changes the hook count between
     two renders of one mounted chip. */
  const press = useRef<{ x: number; y: number; dragging: boolean; ghost: HTMLElement | null } | null>(null);
  const justDragged = useRef(false);
  /* Also a hook, so also above the early return: which KIND the row means while the field is
     empty, since an empty field stores nothing for it to be read back from. */
  const digits = digitsIn(format);
  const [digitKind, setDigitKind] = useState<'sf' | 'dp'>(digits?.kind ?? 'dp');
  const uDigits = digitsIn(uncertaintyFormat);
  const [uDigitKind, setUDigitKind] = useState<'sf' | 'dp'>(uDigits?.kind ?? 'dp');

  const canFormat = !!info;
  // What a chip with no format of its own reads as — the first its source offers, which is
  // `degrees` on a bearing and `decimal` on a coordinate rather than a generic "as written".
  const fallbackFormat = formats[0] ?? 'asWritten';

  const chipClass = status ? STATUS_CLASS[status] : 'ws-answer-ref-chip';
  /*
   * ONE NAME FOR EVERY CHIP: what this reference is, whether it is reachable, and where from.
   *
   * It said "choose how it reads" on a chip with formats, which was an instruction in a name
   * — the slot that is re-announced on every change, where rule 6 puts descriptions instead —
   * and became half the truth when the popup grew a rename. It also meant two chips in one
   * sentence announced in two different shapes depending on what their source offered, which
   * is the audible version of the misalignment the two render branches caused.
   *
   * What the popup holds is the reader's to say: `aria-haspopup` names its kind and
   * `aria-expanded` its state, both of which the shared menu already sets.
   */
  const label = status
    ? t(`blocks.answerRef.chipLabel.${status}`, { name, address: info?.address ?? '' })
    : t('blocks.answerRef.chipLabelPlain', { name });

  /*
   * THE SHARED MENU, with the chip as its trigger.
   *
   * It was hand-rolled first, and hand-rolling it meant rebuilding three things the shared
   * one already has and getting none of them: it stayed open when you clicked away, and it
   * was clipped by the text box it sat in, because a menu positioned inside scrolling,
   * overflow-hidden prose has nowhere to go. `SelectMenu` portals to the body and positions
   * from a rect, which is the escape hatch this app already uses for exactly this. It grew
   * two props to let a caller bring its own trigger rather than being forked.
   */
  // Not an authoring surface: no provider, so nothing to edit and nothing to say about it.
  if (!canFormat) {
    return (
      <NodeViewWrapper as='span' className='inline-block align-middle' data-drag-handle>
        <span contentEditable={false} aria-label={label} className={chipClass}>{name}</span>
      </NodeViewWrapper>
    );
  }

  const current = format ?? fallbackFormat;

  /*
   * THE NAME, EDITED WHERE IT IS READ.
   *
   * It was the panel's alone for a while, on the reasoning that a name belongs to the ALIAS
   * and is shared by every chip quoting it, so editing it from one occurrence edits something
   * the occurrence does not own. That is still true and is still why there is one field and
   * not one per chip: this writes the alias, and every other chip quoting it changes with it.
   * What it is not is a reason to make the teacher leave the sentence to fix a name they are
   * reading in the sentence.
   *
   * CLEARING IT RETURNS THE DERIVED NAME rather than leaving the chip blank, and so does
   * typing that name back — a stored copy of a derived name looks identical and then stops
   * following the source block's own renames. Same rule the manager's field uses, because it
   * is now the same field.
   */
  /*
   * HOW MANY DIGITS THIS OCCURRENCE READS WITH, typed rather than picked.
   *
   * ONE ROW WITH A SWITCHABLE LABEL, which is how the variables panel asks the same question —
   * and the same reasoning applies twice over here. Only one of the two can ever be in force,
   * so two rows meant one of them was always an empty box that could do nothing; and this
   * popup sits in running prose, where every pixel of width is width the sentence loses.
   *
   * `format` holds `sf:3` or `dp:2`, so the exclusivity is in the shape rather than in a rule
   * that has to be enforced. The label is the control that says which kind the number means,
   * and switching it re-reads the same typed count as the other kind.
   *
   * ROUNDING, NEVER THE NUMBER: this changes how the value READS and never the value itself.
   * That is why these do not appear on a chip inside an equation — a rounding chosen for a
   * sentence must not reach a grader. See `acceptsDigits`.
   */
  const kind = digits?.kind ?? digitKind;
  const digitLabel = t(kind === 'sf'
    ? 'blocks.answerRef.format.sigFigsLabel'
    : 'blocks.answerRef.format.decimalsLabel');
  const digitRows = takesDigits && (
    <div className='flex items-center justify-between gap-2'>
      {/* The label IS the switch, as it is in the variables panel: a separate control for
          "which of these two" would be a third thing in a popup that holds two. */}
      <button
        type='button'
        onClick={() => {
          const next = kind === 'sf' ? 'dp' : 'sf';
          setDigitKind(next);
          // The typed count carries across rather than being cleared: 3 places and 3 figures
          // are both legal, and dropping it would make the switch feel like a reset.
          if (digits) updateAttributes({ format: digitFormat(next, digits.n) });
        }}
        /* `text-left` because this is a BUTTON, and a button centres its own text. It shows
           on the long label: "Uncertainty decimals" wraps in this popup and the two lines
           sat centred under each other while every other label in the menu started at the
           same x. Reported as "'uncertainty decimals/sig figs' label for display should be
           left aligned, not center aligned". */
        className='rounded px-0.5 text-left text-xs text-ws-muted underline decoration-dotted hover:text-ws-text-link'
      >
        {digitLabel}
      </button>
      {/*
        * TWO CHARACTERS WIDE, because the answer is one digit and occasionally two.
        *
        * The field was sized by the input's own default of about twenty characters —
        * `NumberField` is an `inline-flex` that grows to its content, so a `w-12` on a wrapper
        * around it constrained nothing. It stretched the popup, which is most of why the menu
        * read as too wide. Nine figures is already more than any answer carries, so the cap is
        * generous at two.
        */}
      <NumberField
        grammar='integer'
        maxLength={2}
        className='w-14 shrink-0'
        ariaLabel={digitLabel}
        value={digits ? String(digits.n) : ''}
        /* Clamped on the way in, as the variables panel does with the same bounds: a field
           that can send what the bounds refuse just loses what was typed. */
        onChange={v => updateAttributes({
          format: v.trim() === ''
            ? null
            : digitFormat(kind, clampDigits(kind, Number(v), digitRowsFor.maxDecimals)),
        })}
      />
    </div>
  );

  /*
   * THE UNCERTAINTY'S OWN ROW — only on a Numerical `full` chip, which is the only chip that
   * prints two numbers.
   *
   * A single count over both halves can write "9.80 ± 0.30" or "9.8 ± 0.3" and never
   * "9.81 ± 0.3", which is the pairing a physicist writes. Two rows let a teacher say it.
   *
   * Nothing derives one from the other: the owner chose independent rows over a
   * convention-driven default, so a teacher can author a pair this app would not pick.
   */
  const uKind = uDigits?.kind ?? uDigitKind;
  const uDigitLabel = t(uKind === 'sf'
    ? 'blocks.answerRef.format.uncertaintySigFigsLabel'
    : 'blocks.answerRef.format.uncertaintyDecimalsLabel');
  const uncertaintyRow = digitRowsFor.uncertainty && (
    <div className='flex items-center justify-between gap-2'>
      <button
        type='button'
        onClick={() => {
          const next = uKind === 'sf' ? 'dp' : 'sf';
          setUDigitKind(next);
          if (uDigits) updateAttributes({ uncertaintyFormat: digitFormat(next, uDigits.n) });
        }}
        className='rounded px-0.5 text-left text-xs text-ws-muted underline decoration-dotted hover:text-ws-text-link'
      >
        {uDigitLabel}
      </button>
      <NumberField
        grammar='integer'
        maxLength={2}
        className='w-14 shrink-0'
        ariaLabel={uDigitLabel}
        value={uDigits ? String(uDigits.n) : ''}
        onChange={v => updateAttributes({
          uncertaintyFormat: v.trim() === ''
            ? null
            : digitFormat(uKind, clampDigits(uKind, Number(v), digitRowsFor.maxDecimals)),
        })}
      />
    </div>
  );

  const renameHeader = (updateAlias || digitRows) && (
    /*
     * CAPPED, because the ADDRESS is what sets this popup's width.
     *
     * Measured when the digit field was blamed for it: the field is 38px and the address line
     * is 250, so the popup came out 264 — a flex column is as wide as its widest child wants
     * to be, and an address wants its whole unwrapped length. It already wraps; it just never
     * had a reason to.
     *
     * 11rem, which is the width of a SHORT NAME and a digit row, because that is what this
     * popup is for: the whole point of the rename field is that a teacher replaces the
     * address with something brief. Sizing the box to hold an unwrapped address would be
     * building for the case the control exists to end. A max rather than a fixed width, so a
     * short address still gets a small popup.
     */
    <div className='flex max-w-44 flex-col gap-1'>
      {/* The rename writes the ALIAS, so it needs the one callback that can; the digit rows
          below write this chip's own attribute and are offered either way. */}
      {updateAlias && <CommitField
        /* THE STORED NAME, with no facet suffix — see `AnswerRefInfo.rawName`. `name` is what
           the CHIP reads, family name and "(full)" together, and a field pre-filled with that
           stores the suffix on the next commit: `test (full)`, then `test (full) (full)`. */
        value={info.rawName}
        placeholder={info.derived}
        // The manager's own label for the same field, which this now IS — a second string
        // saying the same thing is how two surfaces come to call one control two names.
        ariaLabel={t('blocks.answerRef.manager.nameLabel', { name })}
        onCommit={next => updateAlias(aliasId, {
          name: next.trim() === '' || next.trim() === info.derived ? null : next.trim(),
        })}
      />}
      {/*
        * WHICH ANSWER THIS IS, written out: task, block, and the part of its answer.
        *
        * Only where the chip is not already saying it. An unnamed chip READS as its address
        * now, so printing it under the field would be the same string twice, once as the
        * field's own placeholder. A named one shows the teacher's words instead, and that is
        * exactly where the address has nowhere else to be.
        *
        * One line per address, because an alias may hold several: they are the same answer
        * reached different ways, and run together they read as one long place. A chip shows
        * only the first, so several is the other case worth printing.
        */}
      {(info.named || info.addresses.length > 1) && info.addresses.map(line => (
        // WRAPS. An address is a task title and a block name, both of them the teacher's own
        // sentences, and `nowrap` put them off the side of a phone with nothing to scroll.
        <span key={line} className='break-words text-xs text-ws-muted'>{line}</span>
      ))}
      {/* Under the name and the address, because it is the least of the three: which answer
          this is comes before how many of its digits to show. */}
      {digitRows}
      {uncertaintyRow}
    </div>
  );

  /*
   * THE CHIP IS DRAGGED BY HAND, because the native drag it used to get cannot be told apart
   * from a click.
   *
   * `draggable: true` on the node spec made ProseMirror mark the chip's own DOM draggable, and
   * the browser then starts a drag on a movement of a pixel or two — well inside what a press
   * meant as a click contains. The click never arrived and the menu underneath, which holds
   * both the format and the rename, could not be opened at all.
   *
   * Narrowing the handle was tried and cannot work: `data-drag-handle` only tells ProseMirror
   * where ITS mousedown handling applies, while `draggable` sits on the node's DOM and the
   * browser reads that alone. The whole chip stayed draggable, and the element the browser
   * handed to the drop was the bare handle, which carries no alias — so a dropped chip came
   * back empty.
   *
   * So native drag is off and this replaces it. A press under the threshold does nothing at
   * all and the button beneath gets its click; past it, the node is moved with a transaction,
   * which is also what makes the drop exact rather than a reparse of whatever DOM was dragged.
   */
  const endPress = () => {
    press.current?.ghost?.remove();
    press.current = null;
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
  };

  function onMove(e: PointerEvent) {
    const p = press.current;
    if (!p) return;
    if (!p.dragging) {
      if (Math.hypot(e.clientX - p.x, e.clientY - p.y) < DRAG_THRESHOLD_PX) return;
      p.dragging = true;
      // The chip itself, following the pointer — the same affordance `formula-field` draws.
      const ghost = document.createElement('div');
      /* `POPUP_LEVEL`, not a taller number of its own: `popup-level` is explicit that one level
         is the whole contract and a second judgement about a global question is how a popup
         goes quietly invisible. A ghost and a menu are never on screen together anyway — the
         press that makes one is the press that cannot open the other. */
      ghost.className = `pointer-events-none fixed ${POPUP_LEVEL} opacity-90`;
      ghost.append(document.createTextNode(name));
      ghost.style.cssText += ';padding:2px 6px;border-radius:4px;font-size:0.75rem;font-weight:600;';
      document.body.append(ghost);
      p.ghost = ghost;
    }
    if (p.ghost) {
      p.ghost.style.left = `${e.clientX + 10}px`;
      p.ghost.style.top = `${e.clientY - 12}px`;
    }
  }

  function onUp(e: PointerEvent) {
    const p = press.current;
    const dragged = !!p?.dragging;
    endPress();
    if (!dragged || typeof getPos !== 'function') return;
    // The press is spent: stop the click it would otherwise become from opening the menu.
    justDragged.current = true;
    // `getPos` returns undefined for a node view whose node has already left the document.
    const from = getPos();
    if (from == null) return;
    const to = from + node.nodeSize;
    const at = editor.view.posAtCoords({ left: e.clientX, top: e.clientY })?.pos;
    // Dropped on itself is a no-op rather than a delete-and-reinsert, which would churn history.
    if (at == null || (at >= from && at <= to)) return;
    editor.chain().focus().command(({ tr }) => {
      tr.delete(from, to);
      tr.insert(tr.mapping.map(at), node);
      return true;
    }).run();
  }

  return (
    <NodeViewWrapper
      as='span'
      className='inline-block align-middle'
      onPointerDown={(e: React.PointerEvent) => {
        if (e.button !== 0) return;
        /*
         * A PORTAL IS NOT A SUBTREE TO THE DOM AND IS ONE TO REACT.
         *
         * The menu this chip opens — formats, digit rows and the RENAME FIELD — is portalled
         * to `document.body`, so nothing in it is a DOM descendant of the chip. React
         * propagates synthetic events along its own tree regardless, so a press inside that
         * field arrived here and armed the drag: selecting the name to retype it picked the
         * chip up out of the prose instead. Reported as "i click and drag the text in the
         * alias textbox and the chip drags, instead of the text highlighting so i can update
         * it".
         *
         * The DOM containment test is the discriminator, and it is the same one for anything
         * else the menu grows later.
         */
        if (!e.currentTarget.contains(e.target as unknown as globalThis.Node)) return;
        press.current = { x: e.clientX, y: e.clientY, dragging: false, ghost: null };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      }}
      /* CAPTURE, so it is spent before `SelectMenu`'s own handler ever sees it. */
      onClickCapture={(e: React.MouseEvent) => {
        if (!justDragged.current) return;
        justDragged.current = false;
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/*
        * THE ADDRESS, on the wrapper rather than on the trigger.
        *
        * A chip's name is often the block's alone — the task is only prefixed when another
        * task holds a block of the same name — so the chip on its own does not say WHICH
        * task answered, and that is the question a teacher reading someone's prose asks
        * first. The manager's own note puts this on the tooltip, and the chip lost it when it
        * became a menu trigger.
        *
        * A native title on an ancestor serves every descendant that has none of its own, so
        * this reaches the trigger without `SelectMenu` growing a prop for it.
        */}
      <span contentEditable={false} title={info?.address || name}>
        <SelectMenu
          value={current}
          options={formats.map(option => ({
            value: option,
            label: t(`blocks.answerRef.format.${option}`),
            // A tick on the one in force. The shared menu marks it by weight alone, which is
            // hard to read on a two-entry list where there is nothing to compare against.
            icon: option === current ? <Check className='h-3.5 w-3.5' /> : undefined,
          }))}
          onChange={next => updateAttributes({ format: next === fallbackFormat ? null : next })}
          ariaLabel={label}
          triggerLabel={name}
          triggerClassName={chipClass}
          hideChevron
          // The chip carries `vertical-align: middle`, and it is now on the BUTTON with this
          // wrapper between it and the prose — so the wrapper has to align the same way or
          // the chip sits on the wrapper's baseline instead of its own.
          className='align-middle'
          header={renameHeader}
        />
      </span>
    </NodeViewWrapper>
  );
}

export const AnswerRefChip = Node.create<AnswerRefChipOptions>({
  name: 'answerRefChip',

  addOptions() {
    return {};
  },
  group: 'inline',
  inline: true,
  atom: true,
  /*
   * NOT `draggable`, and the node view moves itself instead — see the comment on `press` there.
   * ProseMirror's drag is the browser's, which starts on a pixel of movement and so cannot be
   * told apart from the click that opens this chip's menu.
   */
  draggable: false,

  addAttributes() {
    return {
      /**
       * THE ALIAS, and nothing else. Identity in an attribute, display derived.
       *
       * The chip used to carry the address itself. Moving it to the alias is what makes a
       * rename universal: there is one name because there is one object, so nothing has to be
       * swept when it changes. It also means a chip is only as portable as its alias, which is
       * why a paste has to bring the alias along.
       */
      aliasId: {
        default: '',
        parseHTML: el => el.getAttribute('data-answer-ref-alias') ?? '',
        renderHTML: attrs => ({ 'data-answer-ref-alias': attrs.aliasId }),
      },
      /**
       * The name as last written, for a renderer with no context — and a migration path.
       *
       * Still cosmetic and still never authoritative. Two jobs: a surface that renders stored
       * html raw has something to print, and a chip authored BEFORE aliases existed parses its
       * old baked text into this field, so it shows its old name while reporting itself broken
       * rather than vanishing into the prose around it.
       */
      fallback: {
        default: null,
        parseHTML: el => el.getAttribute('data-answer-ref-fallback') ?? el.textContent ?? null,
        renderHTML: attrs => (attrs.fallback ? { 'data-answer-ref-fallback': attrs.fallback } : {}),
      },
      /**
       * HOW THIS OCCURRENCE READS, as distinct from which answer it quotes.
       *
       * On the CHIP and not on the alias, which is the whole reason it can exist: a facet
       * lives on the alias and is shared by every chip naming it, so "You said Negative" and
       * "...because it was negative" would otherwise need two aliases — two names for one
       * answer, which is the rule the feature rests on.
       *
       * Absent on every chip written before this, and absent is `asWritten`, so nothing that
       * already exists reads differently than it did.
       */
      format: {
        default: null,
        parseHTML: el => el.getAttribute('data-answer-ref-format'),
        renderHTML: attrs => (attrs.format && attrs.format !== 'asWritten'
          ? { 'data-answer-ref-format': attrs.format }
          : {}),
      },
      /*
       * THE UNCERTAINTY'S OWN COUNT — a second attribute rather than a compound token.
       *
       * Only a Numerical `full` chip has two numbers in it, and one spec over both can print
       * "9.80 ± 0.30" or "9.8 ± 0.3" but never "9.81 ± 0.3". Encoding the pair into `format`
       * as `dp:2;dp:1` would make `digitsIn` ambiguous for every other chip in the app; a
       * second attribute is inert everywhere it does not apply.
       *
       * Absent on every chip written before this, and absent means "the value's count covers
       * both", which is exactly how they already read.
       */
      uncertaintyFormat: {
        default: null,
        parseHTML: el => el.getAttribute('data-answer-ref-uncertainty-format'),
        renderHTML: attrs => (attrs.uncertaintyFormat
          ? { 'data-answer-ref-uncertainty-format': attrs.uncertaintyFormat }
          : {}),
      },
    };
  },

  parseHTML() {
    // The legacy tag too, so a pre-alias chip is adopted and reported broken rather than
    // dropped into the prose as a bare word. See `fallback` above.
    return [{ tag: 'span[data-answer-ref-alias]' }, { tag: 'span[data-answer-ref-block]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const name = HTMLAttributes['data-answer-ref-fallback'];
    // The class rides along because it is what draws the chip — pill, tint and link mark alike,
    // all from `globals.css`. Without it a raw render would be a bare word in a sentence.
    return [
      'span',
      mergeAttributes(HTMLAttributes, { class: 'ws-answer-ref-chip' }),
      name ?? '',
    ];
  },

  /**
   * What it becomes outside rich text: `[name]`, brackets and all.
   *
   * Exactly `VariableChip.renderText`'s bargain. A bare name pasted into a plain field stops
   * being a reference and starts being a word, silently — the brackets are what say otherwise.
   * A reference pasted where references are forbidden arrives as this text and is filtered out,
   * leaving the field empty rather than holding something it cannot resolve.
   */
  renderText({ node }) {
    return `[${node.attrs.fallback || node.attrs.aliasId}]`;
  },

  /**
   * ONE PRESS TO DELETE, not two — the same dead-keystroke `VariableChip` fixed.
   *
   * An atom is selectable, so ProseMirror's default Backspace beside one selects it and waits.
   * Inside a coloured chip that selection ring is easy to miss, so the first press reads as
   * nothing happening.
   */
  addKeyboardShortcuts() {
    const removeAdjacent = (side: 'before' | 'after') => () => {
      const { state } = this.editor;
      const { empty, $anchor } = state.selection;
      if (!empty) return false;
      const node = side === 'before' ? $anchor.nodeBefore : $anchor.nodeAfter;
      if (node?.type.name !== this.name) return false;
      const from = side === 'before' ? $anchor.pos - node.nodeSize : $anchor.pos;
      return this.editor.commands.deleteRange({ from, to: from + node.nodeSize });
    };
    return { Backspace: removeAdjacent('before'), Delete: removeAdjacent('after') };
  },

  addNodeView() {
    return ReactNodeViewRenderer(AnswerRefChipNodeView);
  },
});
